import { useState } from 'react';
import firebase from '../firebase/firebaseConfig';

export const useMFA = () => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

    // Initialize reCAPTCHA verifier
    const initializeRecaptcha = (elementId) => {
        try {
            const verifier = new firebase.auth.RecaptchaVerifier(elementId, {
                size: 'invisible',
                callback: () => {
                    // reCAPTCHA solved
                },
                'expired-callback': () => {
                    setError('reCAPTCHA expired. Please try again.');
                }
            });
            setRecaptchaVerifier(verifier);
            return verifier;
        } catch (error) {
            console.error('Error initializing reCAPTCHA:', error);
            setError('Failed to initialize reCAPTCHA');
            return null;
        }
    };

    // Check if user has MFA enabled
    const checkMFAStatus = async () => {
        try {
            const user = firebase.auth().currentUser;
            if (!user) return false;
            
            const enrolledFactors = user.multiFactor.enrolledFactors;
            return enrolledFactors.length > 0;
        } catch (error) {
            console.error('Error checking MFA status:', error);
            return false;
        }
    };

    // Start phone MFA enrollment
    const startPhoneEnrollment = async (phoneNumber, recaptchaContainerId) => {
        setLoading(true);
        setError(null);
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            // Get multi-factor session (required for MFA enrollment)
            const session = await user.multiFactor.getSession();
            
            // Clear any existing reCAPTCHA verifier before creating a new one
            if (recaptchaVerifier) {
                try {
                    recaptchaVerifier.clear();
                } catch (e) {
                    console.warn('Error clearing reCAPTCHA:', e);
                }
                setRecaptchaVerifier(null);
            }
            
            // Always create a fresh reCAPTCHA verifier
            let verifier = null;
            if (recaptchaContainerId) {
                verifier = initializeRecaptcha(recaptchaContainerId);
            }
            
            if (!verifier) {
                throw new Error('reCAPTCHA verifier not initialized');
            }

            // Create phone auth provider
            const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
            
            // For MFA enrollment, we need to pass both the phone number and session
            // This binds the verification to the multi-factor enrollment session
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                {
                    phoneNumber: phoneNumber,
                    session: session
                },
                verifier
            );

            return { verificationId, phoneNumber, session };
        } catch (error) {
            let errorMessage = error.message;
            // Provide more helpful error messages
            if (error.code === 'auth/operation-not-allowed') {
                errorMessage = 'Phone authentication is not enabled. Please enable it in Firebase Console under Authentication > Sign-in method > Phone.';
            } else if (error.code === 'auth/invalid-phone-number') {
                errorMessage = 'Invalid phone number format. Please use format: +1234567890';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many requests. Please try again later.';
            } else if (error.code === 'auth/missing-phone-number') {
                errorMessage = 'Phone number is required.';
            }
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Complete phone MFA enrollment
    const completePhoneEnrollment = async (verificationId, verificationCode) => {
        setLoading(true);
        setError(null);
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            // Create phone auth credential from the verificationId and code
            // The verificationId was already bound to the MFA session when we called verifyPhoneNumber
            const phoneCredential = firebase.auth.PhoneAuthProvider.credential(
                verificationId,
                verificationCode
            );

            // Create multi-factor assertion from the credential
            const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(phoneCredential);

            // Enroll the second factor
            // This uses the current user's multi-factor context
            await user.multiFactor.enroll(multiFactorAssertion, 'Phone Number');

            // Clean up reCAPTCHA
            if (recaptchaVerifier) {
                recaptchaVerifier.clear();
                setRecaptchaVerifier(null);
            }

            return true;
        } catch (error) {
            let errorMessage = error.message;
            // Provide more helpful error messages
            if (error.code === 'auth/invalid-verification-code') {
                errorMessage = 'Invalid verification code. Please check and try again.';
            } else if (error.code === 'auth/code-expired') {
                errorMessage = 'Verification code has expired. Please request a new code.';
            } else if (error.code === 'auth/session-expired') {
                errorMessage = 'Session expired. Please start the enrollment process again.';
            }
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Re-authenticate user (required for sensitive operations like MFA unenrollment)
    // Returns: { success: true } or { requiresMFA: true, resolver: ... }
    const reauthenticateUser = async (password) => {
        try {
            const user = firebase.auth().currentUser;
            if (!user || !user.email) {
                throw new Error('User not authenticated');
            }

            // Create credential with email and password
            const credential = firebase.auth.EmailAuthProvider.credential(
                user.email,
                password
            );

            // Re-authenticate the user
            await user.reauthenticateWithCredential(credential);
            return { success: true };
        } catch (error) {
            // Handle MFA requirement during re-authentication
            if (error.code === 'auth/multi-factor-auth-required') {
                // User has MFA enabled, need to verify MFA to complete re-authentication
                return {
                    requiresMFA: true,
                    resolver: error.resolver
                };
            }
            
            let errorMessage = error.message;
            if (error.code === 'auth/wrong-password') {
                errorMessage = 'Incorrect password. Please try again.';
            } else if (error.code === 'auth/user-mismatch') {
                errorMessage = 'User mismatch. Please log in again.';
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = 'User not found. Please log in again.';
            } else if (error.code === 'auth/invalid-credential') {
                errorMessage = 'Invalid credentials. Please check your password.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later.';
            }
            throw new Error(errorMessage);
        }
    };

    // Complete re-authentication with MFA verification
    const completeReauthenticationWithMFA = async (resolver, verificationCode, verificationId) => {
        try {
            // Create phone auth credential
            const phoneAuthCredential = firebase.auth.PhoneAuthProvider.credential(
                verificationId,
                verificationCode
            );

            // Create multi-factor assertion
            const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(phoneAuthCredential);

            // Complete re-authentication with MFA
            const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
            return userCredential;
        } catch (error) {
            let errorMessage = error.message;
            if (error.code === 'auth/invalid-verification-code') {
                errorMessage = 'Invalid verification code. Please check and try again.';
            } else if (error.code === 'auth/code-expired') {
                errorMessage = 'Verification code has expired. Please request a new code.';
            }
            throw new Error(errorMessage);
        }
    };

    // Unenroll MFA
    // Returns: { success: true } or { requiresMFA: true, resolver: ... }
    const unenrollMFA = async (factorUid, password) => {
        setLoading(true);
        setError(null);
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            // Re-authenticate before unenrolling (required for sensitive operations)
            if (password) {
                const reauthResult = await reauthenticateUser(password);
                
                // If MFA is required for re-authentication, return the resolver
                if (reauthResult.requiresMFA) {
                    setLoading(false);
                    return {
                        requiresMFA: true,
                        resolver: reauthResult.resolver,
                        factorUid: factorUid  // Pass along the factor to unenroll after MFA
                    };
                }
            }

            // Proceed with unenrollment
            await user.multiFactor.unenroll(factorUid);
            return { success: true };
        } catch (error) {
            let errorMessage = error.message;
            if (error.code === 'auth/requires-recent-login') {
                errorMessage = 'This operation requires recent authentication. Please provide your password to continue.';
            }
            setError(errorMessage);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Complete unenrollment after MFA verification
    const completeUnenrollmentAfterMFA = async (factorUid) => {
        setLoading(true);
        setError(null);
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            await user.multiFactor.unenroll(factorUid);
            return { success: true };
        } catch (error) {
            let errorMessage = error.message;
            setError(errorMessage);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Get enrolled factors
    const getEnrolledFactors = () => {
        try {
            const user = firebase.auth().currentUser;
            if (!user) return [];
            
            return user.multiFactor.enrolledFactors;
        } catch (error) {
            console.error('Error getting enrolled factors:', error);
            return [];
        }
    };

    return {
        startPhoneEnrollment,
        completePhoneEnrollment,
        unenrollMFA,
        completeUnenrollmentAfterMFA,
        reauthenticateUser,
        completeReauthenticationWithMFA,
        checkMFAStatus,
        getEnrolledFactors,
        initializeRecaptcha,
        error,
        loading
    };
};

